import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { announcementsAPI, interactionsAPI, hubError } from '../lib/api';
import { useLiveEvents } from '../context/LiveEventsContext';
import { useInteractionTracker, useItemTimeTracker } from '../hooks/useInteractionTracker';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Megaphone, Plus, Edit, Trash2, Loader2, Eye, MousePointer, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const levelMap = {
  info: { label: 'Informativo', color: 'bg-blue-100 text-blue-700' },
  important: { label: 'Importante', color: 'bg-yellow-100 text-yellow-700' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700' },
};

const emptyForm = { title: '', body: '', level: 'info', expires_at: '' };
const LIVE_ANNOUNCEMENT_EVENTS = ['announcement.created'];

export default function AnnouncementsPage() {
  const { selectedChurchId, canCreate, canUpdate, canDelete, canRead } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [badges, setBadges] = useState({});

  const { trackClick } = useInteractionTracker('announcement', null, null, selectedChurchId, { trackPageView: false, trackTimeSpent: false });
  useItemTimeTracker('announcement', editing, form.title, selectedChurchId, dialogOpen);

  const pg = usePagination(String(selectedChurchId));

  const fetchItems = useCallback(() => {
    setLoading(true);
    announcementsAPI.list(undefined, pg.params)
      .then(res => {
        const data = res.data?.data || [];
        setItems(data);
        setTotal(res.data?.total || 0);
        if (data.length > 0 && canRead('analytics')) {
          interactionsAPI.badges('announcement', data.map(e => e.id), selectedChurchId)
            .then(bRes => setBadges(bRes.data?.data || {}))
            .catch(() => {});
        }
      })
      .catch(err => toast.error(hubError(err, 'Error al cargar anuncios')))
      .finally(() => setLoading(false));
  }, [canRead, selectedChurchId, pg.params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useLiveEvents(LIVE_ANNOUNCEMENT_EVENTS, (events) => {
    toast.info(`Nuevo anuncio: ${events[events.length - 1].payload?.title || ''}`);
    fetchItems();
  });

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    trackClick(item.id, item.title);
    setEditing(item.id);
    setForm({
      title: item.title || '', body: item.body || '',
      level: item.level || 'info', expires_at: item.expires_at || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('El título es requerido'); return; }
    if (!form.body) { toast.error('El mensaje es requerido'); return; }
    setSaving(true);
    const payload = { ...form, expires_at: form.expires_at || null };
    try {
      if (editing) { await announcementsAPI.update(editing, payload); toast.success('Anuncio actualizado'); }
      else { await announcementsAPI.create(payload); toast.success('Anuncio publicado'); }
      setDialogOpen(false); fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al guardar')); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await announcementsAPI.delete(id); toast.success('Anuncio eliminado'); fetchItems(); }
    catch (err) { toast.error(hubError(err, 'Error al eliminar')); }
  };

  return (
    <div>
      <TopBar title="Anuncios" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
        actionButton={canCreate('announcements') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={openCreate} data-testid="announcements-create-button"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="anuncios" testId="announcements-pagination"
        />
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><Megaphone className="w-12 h-12 text-muted-foreground/40" /><p>No hay anuncios</p>{canCreate('announcements') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white mt-2" onClick={openCreate}>Crear anuncio</Button>}</div>
        ) : (
          <div className="space-y-3">
            {items.map(item => {
              const b = badges[String(item.id)];
              const level = levelMap[item.level] || levelMap.info;
              return (
                <Card key={item.id} className="stat-card">
                  <CardContent className="p-4 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-[#056A84]/10 flex items-center justify-center shrink-0">
                        <Megaphone className="w-5 h-5 text-[#056A84]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm">{item.title}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${level.color}`}>{level.label}</span>
                        </div>
                        {item.body && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.body}</p>}
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground">{item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES') : ''}</span>
                          {item.expires_at && <span className="text-xs text-muted-foreground">Vence: {new Date(item.expires_at).toLocaleDateString('es-ES')}</span>}
                          {b && (
                            <TooltipProvider>
                              <div className="flex items-center gap-2" data-testid={`announcement-badges-${item.id}`}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                                      <Eye className="w-3 h-3 text-[#056A84]" /><span className="font-medium">{b.views}</span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Vistas</p></TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                                      <MousePointer className="w-3 h-3 text-[#1F9D7A]" /><span className="font-medium">{b.clicks}</span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Clics</p></TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                                      <Users className="w-3 h-3 text-[#2B7DBA]" /><span className="font-medium">{b.unique_users}</span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Usuarios únicos</p></TooltipContent>
                                </Tooltip>
                              </div>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {canUpdate('announcements') && <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`announcement-edit-${item.id}`}><Edit className="w-4 h-4" /></Button>}
                      {canDelete('announcements') && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`announcement-delete-${item.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>¿Eliminar anuncio?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nuevo'} Anuncio</DialogTitle><DialogDescription className="sr-only">Formulario de anuncio</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Título *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="announcement-title-input" /></div>
            <div className="space-y-2"><Label>Mensaje *</Label><Textarea value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={4} data-testid="announcement-body-input" /></div>
            <div className="space-y-2">
              <Label>Nivel</Label>
              <Select value={form.level} onValueChange={v => setForm({...form, level: v})}>
                <SelectTrigger data-testid="announcement-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Informativo</SelectItem>
                  <SelectItem value="important">Importante</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Vence el</Label><Input type="date" value={form.expires_at ? String(form.expires_at).slice(0, 10) : ''} onChange={e => setForm({...form, expires_at: e.target.value})} data-testid="announcement-expires-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="announcement-save-button">{saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}{editing ? 'Actualizar' : 'Publicar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
